import { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { useVoiceCoach } from '@/hooks/useVoiceCoach';
import { logger } from '@/lib/logger';
import { MessageSquare, Volume2, VolumeX, Zap, TrendingUp, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface RealTimeAICoachProps {
  currentBPM: number;
  currentPace: number; // min/km
  targetPace?: number;
  zone?: string;
  distance: number;
  duration: number;
  isRunning: boolean;
  elevation?: number;
  className?: string;
}

interface CoachMessage {
  text: string;
  type: 'encouragement' | 'warning' | 'technical' | 'milestone';
  timestamp: number;
}

export const RealTimeAICoach = ({
  currentBPM,
  currentPace,
  targetPace,
  zone = 'aerobic',
  distance,
  duration,
  isRunning,
  elevation = 0,
  className
}: RealTimeAICoachProps) => {
  const { speakCoach, preferences } = useVoiceCoach();
  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [lastFeedbackTime, setLastFeedbackTime] = useState(0);
  const [isEnabled, setIsEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const currentMessage = messages[messages.length - 1];

  // Determinar frequência de feedback baseado nas preferências
  const feedbackInterval = preferences?.voiceFrequency === 'frequent' ? 60000 : // 1 min
                          preferences?.voiceFrequency === 'minimal' ? 300000 : // 5 min
                          120000; // 2 min (normal)

  const addMessage = useCallback((text: string, type: CoachMessage['type']) => {
    const newMessage = { text, type, timestamp: Date.now() };
    setMessages(prev => [...prev.slice(-4), newMessage]); // Keep last 5
    
    logger.info('AI Coach message', {
      component: 'RealTimeAICoach',
      type,
      message: text
    });
  }, []);

  const provideFeedback = useCallback(async (text: string, type: CoachMessage['type']) => {
    if (!isEnabled || !isRunning) return;

    const now = Date.now();
    if (now - lastFeedbackTime < feedbackInterval) return;

    addMessage(text, type);
    setLastFeedbackTime(now);

    // Falar apenas se voice coaching estiver habilitado
    if (preferences?.voiceEnabled) {
      setIsSpeaking(true);
      try {
        await speakCoach(text);
      } finally {
        setIsSpeaking(false);
      }
    }
  }, [isEnabled, isRunning, lastFeedbackTime, feedbackInterval, preferences, speakCoach, addMessage]);

  // Análise contextual inteligente
  useEffect(() => {
    if (!isRunning || !isEnabled) return;

    const analyzePerformance = async () => {
      // 1. Verificar BPM - zona cardíaca
      if (zone === 'anaerobic' && currentBPM > 175) {
        provideFeedback(
          "Seu batimento está muito alto! Reduza o ritmo e respire fundo.",
          'warning'
        );
        return;
      }

      if (zone === 'aerobic' && currentBPM < 100) {
        provideFeedback(
          "Você pode acelerar um pouco. Seu coração está confortável!",
          'encouragement'
        );
        return;
      }

      // 2. Verificar pace vs target
      if (targetPace && currentPace) {
        const paceDiff = currentPace - targetPace;
        
        if (paceDiff > 0.5) { // Muito lento
          provideFeedback(
            `Você está ${Math.abs(paceDiff * 60).toFixed(0)} segundos mais lento que o planejado. Acelere gradualmente!`,
            'technical'
          );
          return;
        }
        
        if (paceDiff < -0.5) { // Muito rápido
          provideFeedback(
            `Atenção! Você está acelerando demais. Reduza ${Math.abs(paceDiff * 60).toFixed(0)} segundos por km.`,
            'warning'
          );
          return;
        }

        // Pace perfeito
        if (Math.abs(paceDiff) <= 0.1 && Math.random() < 0.3) {
          provideFeedback(
            "Ritmo perfeito! Continue exatamente assim.",
            'encouragement'
          );
          return;
        }
      }

      // 3. Milestones de distância
      const kmMilestones = [1, 3, 5, 10, 15, 21];
      const currentKm = Math.floor(distance);
      
      if (kmMilestones.includes(currentKm) && distance - currentKm < 0.05) {
        const avgPace = duration / distance / 60; // min/km
        provideFeedback(
          `${currentKm}km completos! Pace médio: ${avgPace.toFixed(2)} min/km. Excelente!`,
          'milestone'
        );
        return;
      }

      // 4. Detecção de fadiga (BPM subindo mas pace caindo)
      // Implementar lógica histórica em futuro update

      // 5. Elevação (subidas)
      if (elevation > 50) { // Subida significativa
        provideFeedback(
          "Subida detectada! Encurte os passos e mantenha a cadência.",
          'technical'
        );
        return;
      }

      // 6. Feedback motivacional a cada X minutos
      const durationMinutes = Math.floor(duration / 60);
      if (durationMinutes > 0 && durationMinutes % 10 === 0 && duration % 60 < 5) {
        provideFeedback(
          `${durationMinutes} minutos! Você está indo muito bem. Continue firme!`,
          'encouragement'
        );
      }
    };

    // Verificar condições a cada 10 segundos
    const interval = setInterval(analyzePerformance, 10000);
    
    return () => clearInterval(interval);
  }, [isRunning, currentBPM, currentPace, targetPace, zone, distance, duration, elevation, isEnabled, provideFeedback]);

  if (!isRunning) {
    return (
      <Card className={cn("p-4", className)}>
        <div className="flex items-center gap-3 text-muted-foreground">
          <MessageSquare className="w-5 h-5" />
          <p className="text-sm">AI Coach estará ativo durante a corrida</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={cn("p-4 space-y-3", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-2 h-2 rounded-full",
            isSpeaking ? "bg-green-500 animate-pulse" : "bg-muted"
          )} />
          <h3 className="font-semibold text-sm">AI Coach</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsEnabled(!isEnabled)}
        >
          {isEnabled ? (
            <Volume2 className="w-4 h-4 text-primary" />
          ) : (
            <VolumeX className="w-4 h-4 text-muted-foreground" />
          )}
        </Button>
      </div>

      {/* Current Message */}
      {currentMessage && (
        <div className={cn(
          "p-3 rounded-lg flex items-start gap-3",
          currentMessage.type === 'warning' && "bg-orange-500/10 border border-orange-500/20",
          currentMessage.type === 'encouragement' && "bg-green-500/10 border border-green-500/20",
          currentMessage.type === 'technical' && "bg-blue-500/10 border border-blue-500/20",
          currentMessage.type === 'milestone' && "bg-purple-500/10 border border-purple-500/20"
        )}>
          {currentMessage.type === 'warning' && <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />}
          {currentMessage.type === 'encouragement' && <Zap className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />}
          {currentMessage.type === 'technical' && <TrendingUp className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />}
          {currentMessage.type === 'milestone' && <MessageSquare className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />}
          
          <div className="flex-1">
            <p className="text-sm">{currentMessage.text}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {new Date(currentMessage.timestamp).toLocaleTimeString('pt-BR', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </p>
          </div>
        </div>
      )}

      {/* Status Indicators */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground pt-2 border-t">
        <span className="flex items-center gap-1">
          <div className={cn(
            "w-2 h-2 rounded-full",
            currentBPM > 160 ? "bg-orange-500" : 
            currentBPM > 120 ? "bg-green-500" : "bg-blue-500"
          )} />
          BPM {currentBPM}
        </span>
        <span>•</span>
        <span>Zona: {zone}</span>
        {targetPace && (
          <>
            <span>•</span>
            <span className={cn(
              Math.abs(currentPace - targetPace) > 0.3 && "text-orange-500 font-medium"
            )}>
              Target: {targetPace.toFixed(2)} min/km
            </span>
          </>
        )}
      </div>

      {/* Recent Messages History */}
      {messages.length > 1 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            Ver mensagens anteriores ({messages.length - 1})
          </summary>
          <div className="mt-2 space-y-2">
            {messages.slice(0, -1).reverse().map((msg, i) => (
              <div key={i} className="p-2 bg-muted/50 rounded text-muted-foreground">
                <p>{msg.text}</p>
              </div>
            ))}
          </div>
        </details>
      )}
    </Card>
  );
};